import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment';
import { AuthenticationService } from './auth.service';

type SocketTransports = Array<'polling' | 'websocket'>;

export interface MonitoreoConnectedPayload {
  message: string;
  userId?: string;
  cliente?: unknown;
  rooms?: unknown;
}

@Injectable({ providedIn: 'root' })
export class MonitoreoSocketService implements OnDestroy {
  private socket: Socket | null = null;

  private readonly connectedSubject = new BehaviorSubject<boolean>(false);
  readonly connected$: Observable<boolean> = this.connectedSubject.asObservable();

  private readonly connectedPayloadSubject = new BehaviorSubject<MonitoreoConnectedPayload | null>(null);
  readonly connectedPayload$: Observable<MonitoreoConnectedPayload | null> = this.connectedPayloadSubject.asObservable();

  private readonly newRechargeSubject = new Subject<any>();
  readonly newRecharge$ = this.newRechargeSubject.asObservable();

  private readonly asignarSaldoSubject = new Subject<any>();
  readonly asignarSaldo$ = this.asignarSaldoSubject.asObservable();

  private readonly newAnalitycsSubject = new Subject<any>();
  readonly newAnalitycs$ = this.newAnalitycsSubject.asObservable();

  private readonly ticketRegistradoSubject = new Subject<any>();
  readonly ticketRegistrado$ = this.ticketRegistradoSubject.asObservable();

  private readonly movimientoRegistradoSubject = new Subject<any>();
  readonly movimientoRegistrado$ = this.movimientoRegistradoSubject.asObservable();

  /**
   * Hook: emite cuando llega cualquiera de:
   * - find-products-by-ids
   * - find-products-by-id
   */
  private readonly productosActualizadosSubject = new Subject<{ event: 'find-products-by-ids' | 'find-products-by-id'; payload: any }>();
  readonly productosActualizados$ = this.productosActualizadosSubject.asObservable();

  constructor(
    private readonly auth: AuthenticationService,
    private readonly zone: NgZone,
  ) { }

  conectar(): void {
    const env: any = environment as any;

    if (env?.monitoreoSocketEnabled === false) {
      this.zone.run(() => {
        this.connectedSubject.next(false);
        this.connectedPayloadSubject.next(null);
      });
      return;
    }

    const token = this.getTokenForHandshake();
    if (!token) {
      this.zone.run(() => {
        this.connectedSubject.next(false);
        this.connectedPayloadSubject.next(null);
      });
      return;
    }

    // Si ya existe un socket, debemos resetearlo por completo y forzar uno nuevo
    if (this.socket) {
      this.hardResetSocket(this.socket);
      this.socket = null;
    }

    const namespace = '/monitoreo';
    const { baseUrl, usingDevProxy } = this.resolveBaseUrl();
    const path = this.resolveSocketIoPath();
    const transports: SocketTransports = usingDevProxy ? ['polling'] : ['polling', 'websocket'];
    // Algunos backends/proxies soportan polling pero cierran el upgrade a WS.
    // Permite forzar "solo polling" o desactivar upgrade para evitar errores tipo:
    // "WebSocket is closed before the connection is established."
    const forcePolling = env?.monitoreoSocketForcePolling === true;
    const disableUpgrade = env?.monitoreoSocketDisableUpgrade === true;
    const finalTransports: SocketTransports = forcePolling ? ['polling'] : transports;

    this.socket = io(`${baseUrl}${namespace}`, {
      path,
      auth: { token },
      forceNew: true,
      transports: finalTransports,
      upgrade: !(disableUpgrade || forcePolling),
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
    });

    this.bindListeners(this.socket);
  }

  desconectar(): void {
    if (!this.socket) {
      this.zone.run(() => {
        this.connectedSubject.next(false);
        this.connectedPayloadSubject.next(null);
      });
      return;
    }

    this.hardResetSocket(this.socket);
    this.socket = null;

    this.zone.run(() => {
      this.connectedSubject.next(false);
      this.connectedPayloadSubject.next(null);
    });
  }

  ngOnDestroy(): void {
    this.desconectar();
    this.connectedSubject.complete();
    this.connectedPayloadSubject.complete();
    this.newRechargeSubject.complete();
    this.asignarSaldoSubject.complete();
    this.newAnalitycsSubject.complete();
    this.ticketRegistradoSubject.complete();
    this.movimientoRegistradoSubject.complete();
    this.productosActualizadosSubject.complete();
  }

  private bindListeners(socket: Socket): void {
    const env: any = environment as any;
    const debug = !environment.production && env?.monitoreoSocketDebug === true;

    socket.on('connect', () => {
      this.zone.run(() => this.connectedSubject.next(true));
      if (debug) console.log('[monitoreo] connect', socket.id);
    });

    socket.on('connected', (payload: MonitoreoConnectedPayload) => {
      this.zone.run(() => this.connectedPayloadSubject.next(payload ?? null));
      if (debug) console.log('[monitoreo] connected payload', payload);
    });

    socket.on('connect_error', () => {
      this.zone.run(() => {
        this.connectedSubject.next(false);
        this.connectedPayloadSubject.next(null);
      });
      if (debug) console.warn('[monitoreo] connect_error');
    });

    // Requisito: al desconectar, deshabilitar reconnection y limpiar estado
    socket.on('disconnect', () => {
      try {
        socket.io.opts.reconnection = false;
      } catch {
        // noop
      }
      this.zone.run(() => {
        this.connectedSubject.next(false);
        this.connectedPayloadSubject.next(null);
      });
      if (debug) console.warn('[monitoreo] disconnect');
    });

    if (debug) {
      socket.onAny((eventName: string, ...args: any[]) => {
        // No spamear demasiado payload
        console.log('[monitoreo] event', eventName, args?.[0]);
      });
    }

    // Eventos del servidor a escuchar
    socket.on('newRecharge', (data: any) => this.zone.run(() => this.newRechargeSubject.next(data)));
    socket.on('asignarSaldo', (data: any) => this.zone.run(() => this.asignarSaldoSubject.next(data)));
    socket.on('newAnalitycs', (data: any) => this.zone.run(() => this.newAnalitycsSubject.next(data)));

    // Eventos adicionales pedidos (nombres EXACTOS)
    socket.on('ticket-registrado', (data: any) => this.zone.run(() => this.ticketRegistradoSubject.next(data)));
    socket.on('movimiento-registrado', (data: any) => this.zone.run(() => this.movimientoRegistradoSubject.next(data)));

    socket.on('find-products-by-ids', (payload: any) => {
      this.zone.run(() => this.productosActualizadosSubject.next({ event: 'find-products-by-ids', payload }));
    });
    socket.on('find-products-by-id', (payload: any) => {
      this.zone.run(() => this.productosActualizadosSubject.next({ event: 'find-products-by-id', payload }));
    });
  }

  private getTokenForHandshake(): string | null {
    const tokenFromAuth = this.auth.getToken?.();
    const tokenFromSession = sessionStorage.getItem('token');

    const token = tokenFromAuth || tokenFromSession;
    if (!token) return null;

    if (typeof token === 'string') {
      const trimmed = token.trim();
      if (!trimmed) return null;
      // Si viene JSON stringificado desde sessionStorage, intentamos extraer el valor "real"
      if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (typeof parsed === 'string') return parsed.trim() || null;
          return JSON.stringify(parsed);
        } catch {
          return trimmed;
        }
      }
      return trimmed;
    }

    // getToken() en este proyecto suele devolver JSON ya parseado
    try {
      return JSON.stringify(token);
    } catch {
      return String(token);
    }
  }

  private resolveBaseUrl(): { baseUrl: string; usingDevProxy: boolean } {
    const env: any = environment as any;

    const normalize = (url: string) => url.replace(/\/+$/, '');

    const wsUrl: string | undefined = env?.wsUrl;
    if (wsUrl) return { baseUrl: normalize(wsUrl), usingDevProxy: false };

    const apiUrl: string | undefined = env?.apiUrl;
    if (apiUrl) return { baseUrl: normalize(apiUrl), usingDevProxy: false };

    const useDevProxy = !environment.production && env?.monitoreoSocketUseDevProxy === true;
    if (useDevProxy) return { baseUrl: normalize(window.location.origin), usingDevProxy: true };

    const apiSecurity = String(env?.API_SECURITY ?? '').trim();
    const derived = normalize(apiSecurity).replace(/\/api\/?$/i, '');
    return { baseUrl: derived, usingDevProxy: false };
  }

  private resolveSocketIoPath(): string {
    const env: any = environment as any;
    const raw = (env?.socketIoPath ?? '/socket.io') as string;
    if (!raw) return '/socket.io';
    return raw.startsWith('/') ? raw : `/${raw}`;
  }

  private hardResetSocket(socket: Socket): void {
    try {
      socket.io.opts.reconnection = false;
    } catch {
      // noop
    }

    try {
      socket.removeAllListeners();
    } catch {
      // noop
    }

    try {
      socket.io.removeAllListeners();
    } catch {
      // noop
    }

    try {
      // cerrar Engine.IO explícitamente (requisito)
      socket.io.engine?.close();
    } catch {
      // noop
    }

    try {
      socket.disconnect();
    } catch {
      // noop
    }
  }
}

